import { ref, watch, type Ref } from "vue";
import deepClone from "../utils/deepClone";
import deepEquals from "../utils/deepEquals";
import isNonEmpty from "../utils/isNonEmpty";

/**
 * Generic composable for tracking changes to a model with dirty state.
 * Handles initial value capture, deep comparison, save, and reset logic.
 * 
 * @param initialModel - Optional initial value for the model
 * @returns Object with model, initial, isDirty refs and helper functions
 */
export function useDirtyTracking<T>(initialModel?: T) {
    const model = ref<T>(initialModel as T) as Ref<T>;
    const initial = ref<T | undefined>();
    const isDirty = ref(false);

    watch(model, (val) => {
        if (initial.value == null && isNonEmpty(val)) {
            initial.value = deepClone(val) as T;
        }
    }, { immediate: true });

    watch(model, (val) => {
        if (!initial.value) {
            isDirty.value = false;
            return;
        }
        isDirty.value = !deepEquals(val, initial.value);
    }, { deep: true, immediate: true });

    /**
     * Mark the current state as saved by updating initial to match current model.
     * This resets the dirty flag.
     */
    function markSaved() {
        initial.value = deepClone(model.value) as T;
        isDirty.value = false;
    }

    /**
     * Reset the model to the initial saved state.
     * This resets the dirty flag.
     */
    function resetAll() {
        if (initial.value !== undefined) {
            model.value = deepClone(initial.value) as T;
        }
        isDirty.value = false;
    }

    return {
        model,
        initial,
        isDirty,
        markSaved,
        resetAll,
    };
}

export default useDirtyTracking;

